import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../lib/api';
import { setupSessionInterceptor } from '../hooks/useSessionManager';

const AuthContext = createContext(null);

// Permission refresh interval (30 seconds)
const PERMISSION_REFRESH_INTERVAL = 30000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshIntervalRef = useRef(null);

  // Function to refresh user data and permissions
  const refreshUserData = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    try {
      const response = await authAPI.getCurrentUser();
      const freshUserData = response.data;
      
      // Get current user data from localStorage
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      // BUGFIX: Preserve existing module_permissions if server returns null
      // The /api/users/me endpoint may not return permissions, but login does
      // Don't overwrite valid permissions with null
      if (freshUserData.module_permissions === null && currentUser.module_permissions) {
        console.log('[AuthContext] Preserving existing module_permissions (server returned null)');
        freshUserData.module_permissions = currentUser.module_permissions;
      }
      
      // Same for department_permissions
      if (freshUserData.department_permissions === null && currentUser.department_permissions) {
        console.log('[AuthContext] Preserving existing department_permissions (server returned null)');
        freshUserData.department_permissions = currentUser.department_permissions;
      }
      
      // Check if permissions have changed
      const permissionsChanged = JSON.stringify(currentUser.module_permissions) !== JSON.stringify(freshUserData.module_permissions);
      
      if (permissionsChanged) {
        console.log('[AuthContext] Permissions updated from server');
      }
      
      // Update with fresh data (permissions preserved if server returned null)
      setUser(freshUserData);
      localStorage.setItem('user', JSON.stringify(freshUserData));
    } catch (error) {
      console.error('[AuthContext] Failed to refresh user data:', error);
      // If token is invalid, logout
      if (error.response?.status === 401) {
        // Clear tokens and redirect — don't call logout() to avoid stale closure
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        setUser(null);
      }
    }
  }, []);

  // Start periodic permission refresh
  const startPermissionRefresh = useCallback(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    
    // Set up new interval
    refreshIntervalRef.current = setInterval(() => {
      refreshUserData();
    }, PERMISSION_REFRESH_INTERVAL);
    
    console.log('[AuthContext] Permission refresh started (every 30s)');
  }, [refreshUserData]);

  // Stop periodic permission refresh
  const stopPermissionRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
      console.log('[AuthContext] Permission refresh stopped');
    }
  }, []);

  useEffect(() => {
    // Check if user is logged in on mount
    const token = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      const parsedSavedUser = JSON.parse(savedUser);
      setUser(parsedSavedUser);
      // Verify token is still valid and get fresh data
      authAPI.getCurrentUser()
        .then(response => {
          const freshUserData = response.data;
          
          // BUGFIX: Preserve existing permissions if server returns null
          // The /api/users/me endpoint may not return permissions, but login does
          if (freshUserData.module_permissions === null && parsedSavedUser.module_permissions) {
            console.log('[AuthContext] Init: Preserving module_permissions from localStorage');
            freshUserData.module_permissions = parsedSavedUser.module_permissions;
          }
          if (freshUserData.department_permissions === null && parsedSavedUser.department_permissions) {
            console.log('[AuthContext] Init: Preserving department_permissions from localStorage');
            freshUserData.department_permissions = parsedSavedUser.department_permissions;
          }
          
          setUser(freshUserData);
          localStorage.setItem('user', JSON.stringify(freshUserData));
          // Start periodic refresh after successful login verification
          startPermissionRefresh();
        })
        .catch((error) => {
          // ONLY logout on definitive 401 — transient errors (network, 500) should NOT
          // destroy the session. The user already has a valid cached session.
          if (error.response?.status === 401) {
            console.warn('[AuthContext] Token invalid (401) — logging out');
            logout();
          } else {
            console.warn('[AuthContext] Token verification failed (non-401) — keeping session:', error.message);
            // Keep the cached user, start periodic refresh so it retries later
            startPermissionRefresh();
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }

    // Cleanup on unmount
    return () => {
      stopPermissionRefresh();
    };
  }, [startPermissionRefresh, stopPermissionRefresh]);

  // Also refresh on window focus (when user comes back to tab)
  useEffect(() => {
    const handleFocus = () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        console.log('[AuthContext] Window focused - refreshing permissions');
        refreshUserData();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshUserData]);

  const login = async (email, password, rememberMe = false) => {
    const response = await authAPI.login({ email, password, remember_me: rememberMe });
    const { access_token, refresh_token, user: userData } = response.data;
    
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (rememberMe) localStorage.setItem('remember_me', 'true');
    else localStorage.removeItem('remember_me');
    
    setUser(userData);
    // Start periodic permission refresh after login
    startPermissionRefresh();
    return userData;
  };

  const signup = async (data) => {
    const response = await authAPI.signup(data);
    const { access_token, refresh_token, user: userData } = response.data;
    
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    setUser(userData);
    // Start periodic permission refresh after signup
    startPermissionRefresh();
    return userData;
  };

  const logout = useCallback(() => {
    // Stop permission refresh before logout
    stopPermissionRefresh();
    
    // Call backend to invalidate session
    const token = localStorage.getItem('access_token');
    if (token) {
      authAPI.logout().catch(() => {}); // Ignore errors on logout
    }
    
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
  }, [stopPermissionRefresh]);

  // Update user data (used by onboarding and other flows)
  const updateUser = useCallback((newUserData) => {
    setUser(prevUser => {
      const updatedUser = { ...prevUser, ...newUserData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    });
  }, []);

  // Setup session interceptor on mount
  useEffect(() => {
    setupSessionInterceptor(logout);
  }, [logout]);

  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    updateUser, // For updating user data after onboarding
    refreshUserData, // Expose for manual refresh if needed
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
